import './Invoicing.scss';
import cn from 'classnames';
import { addDays, format, parseISO } from 'date-fns';
import Papa from 'papaparse';
import { ChangeEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { Form, Modal, OverlayTrigger, Popover, Table, Tooltip } from 'react-bootstrap';
import {
  MdCheck,
  MdClear,
  MdOutlineAdsClick,
  MdRefresh,
  MdSearch,
  MdSend,
  MdWarning
} from 'react-icons/md';
import { CgHeadset } from 'react-icons/cg';
import { FiCast } from 'react-icons/fi';
import { RiTvLine } from 'react-icons/ri';
import { GetFeatureExperimentsResponse } from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Intent, Placement, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { EntityKind, EntityLogo, LogoVariant } from '@Components/EntityLogo';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { TableLoading } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { AlertDialog } from '@src/Components/AlertDialog/AlertDialog';
import { useSetError } from '../../redux/modals';
import { staggeredAll } from '../../utils/async';
import { download } from '../../utils/download-utils';
import {
  AdminLambdaFetch,
  awaitJSON,
  DagLambdaFetch,
  fetchCometSelfInvoke,
  LinearLambdaFetch,
  MiscLambdaFetch,
  pollS3,
  StreamingDeliveryLambdaFetch,
  UsersLambdaFetch
} from '../../utils/fetch-utils';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { AssetTraffickingUploadModal } from './AssetTraffickingUploadModal';
import {
  CheckerResult,
  DeliveryComparisonRow,
  GenerateLinearReportInput,
  InvoicingCompany,
  InvoicingInfo,
  InvoicingSummaryCompany,
  MediaType,
  MediaTypeType,
  SPEND_DELTA_THRESHOLD,
  StreamingDeliveryNetwork
} from './invoicing-constants';
import {
  buildInvoicingSummaryRows,
  defaultInvoicingDates,
  InvoicingDateRange,
  toCost
} from './invoicing-utils';
import { useInvoicingCompanies } from './use-invoicing-companies';

type CheckerMap = Record<string, Partial<CheckerResult>>;
type GeneratingCSVMap = Record<
  string,
  { downloadingLinear?: boolean; downloadingIconLinear?: boolean; downloadingStreaming?: boolean }
>;

const Check = () => (
  <div className='result pass'>
    <MdCheck />
  </div>
);
const X = () => (
  <div className='result fail'>
    <MdClear />
  </div>
);
const Warning = () => (
  <div className='result warning'>
    <MdWarning />
  </div>
);

interface CompanyCellProps {
  cid: string;
  name: string;
}

const CompanyCell = ({ cid, name }: CompanyCellProps) => (
  <div className='company-column'>
    <EntityLogo
      kind={EntityKind.COMPANY}
      id={cid}
      name={name}
      size={Size.LG}
      variant={LogoVariant.WIDE}
    />
    <div>{name}</div>
  </div>
);

interface CheckerResultsCellProps {
  row: InvoicingCompany;
  checkerInfo?: Partial<CheckerResult>;
  companySpendTotal: number;
  loading: boolean;
  onRecheck: () => void;
  onShowComparison: () => void;
}

// bpm-frontend's legacy Invoicing page keys its per-channel delivery totals by media type
// ("streaming" | "audio" | "display") but reads the display channel back under a different key
// ("olv"), so olvDeliv is always 0. Preserved as-is for behavioral parity with production.
const CheckerResultsCell = ({
  row,
  checkerInfo,
  companySpendTotal,
  loading,
  onRecheck,
  onShowComparison
}: CheckerResultsCellProps): ReactNode => {
  const hasLinear = row.MediaTypes.includes(MediaType.TV);
  const hasStreaming = row.MediaTypes.includes(MediaType.STREAMING);

  let checkerColumnBody: ReactNode;
  let checkerResultIcon: ReactNode;

  if (!checkerInfo) {
    checkerColumnBody = <div className='ready-to-check'>Ready to Check</div>;
  } else if (checkerInfo.checking) {
    checkerColumnBody = <div className='ready-to-check'>Checking...</div>;
  } else if (checkerInfo.needsWizard) {
    checkerColumnBody = <div className='ready-to-check'>Wizarding: Incomplete</div>;
    checkerResultIcon = <Warning />;
  } else {
    checkerResultIcon = <Check />;
    const {
      linearSpotDetailCount = 0,
      strDeliv = 0,
      audioDeliv = 0,
      olvDeliv = 0,
      invoiceSpend = 0
    } = checkerInfo;

    const linearGood = !hasLinear || linearSpotDetailCount === 0;
    let linearBody: ReactNode;
    if (!hasLinear) {
      linearBody = (
        <div className='pass logs'>
          <span>Unresolved Post Log </span>
          <span>Spots: Unapplicable</span>
        </div>
      );
    } else if (linearGood) {
      linearBody = (
        <div className='pass logs'>
          <span>Unresolved Post Log </span>
          <span>Spots: None</span>
        </div>
      );
    } else {
      linearBody = (
        <div className='fail logs'>
          <span>Unresolved Post Log </span>
          <span>Spots: {linearSpotDetailCount}</span>
        </div>
      );
    }

    const spendTotal = Math.round(companySpendTotal);
    const spendDelta = Math.abs(invoiceSpend - spendTotal);
    const streamingGood = spendDelta === 0;
    const streamingOK = spendDelta < SPEND_DELTA_THRESHOLD;
    const deliveryTotal = strDeliv + audioDeliv + olvDeliv;
    const delivGood = deliveryTotal === invoiceSpend;

    let streamingBody: ReactNode;
    if (!hasStreaming) {
      streamingBody = <div className='streaming pass'>Streaming Spend: Unapplicable</div>;
    } else {
      let color: string;
      if (streamingGood) {
        color = 'pass';
      } else if (streamingOK) {
        checkerResultIcon = <Warning />;
        color = 'warning';
      } else {
        checkerResultIcon = <X />;
        color = 'fail';
      }

      streamingBody = (
        <div className={cn('streaming', color)}>
          <div className='streaming-spend'>
            {`Streaming Spend: Invoice: ${toCost(invoiceSpend)}, Spend Table: ${toCost(
              spendTotal
            )}, Δ: ${toCost(spendDelta)}`}
          </div>
          <div className={cn('delivery-spend', delivGood ? 'pass' : 'warning')}>
            {`Delivery: ${toCost(strDeliv)} (Str) + ${toCost(audioDeliv)} (Aud) + ${toCost(
              olvDeliv
            )} (OLV) = ${toCost(deliveryTotal)}`}
            {!delivGood && (
              <IconButton
                className='modal-button'
                data-testid={`show-comparison-button-${row.cid}`}
                size={Size.LG}
                loading={loading}
                onClick={onShowComparison}
              >
                <MdSearch />
              </IconButton>
            )}
          </div>
        </div>
      );
    }

    checkerColumnBody = (
      <>
        <div className='pass'>Wizarding: Complete</div>
        {linearBody}
        {streamingBody}
      </>
    );
  }

  return (
    <div className='checker-results-column'>
      {checkerColumnBody}
      {checkerResultIcon}
      <IconButton
        className='recheck'
        data-testid={`recheck-button-${row.cid}`}
        size={Size.LG}
        disabled={Boolean(checkerInfo?.checking)}
        onClick={onRecheck}
      >
        <MdRefresh />
      </IconButton>
    </div>
  );
};

interface DownloadColumnCellProps {
  row: InvoicingCompany;
  enableIconLinearBillingCompanies: string[];
  generatingCSV?: GeneratingCSVMap[string];
  onDownloadLinear: (isIconNetworks: boolean) => void;
  onDownloadMediaType: (mediaType: MediaTypeType) => void;
}

const DOWNLOADABLE_MEDIA_TYPES: MediaTypeType[] = [
  MediaType.STREAMING,
  MediaType.AUDIO,
  MediaType.DISPLAY
];

const DownloadColumnCell = ({
  row,
  enableIconLinearBillingCompanies,
  generatingCSV,
  onDownloadLinear,
  onDownloadMediaType
}: DownloadColumnCellProps): ReactNode => {
  const hasLinear = row.MediaTypes.includes(MediaType.TV);

  return (
    <div className='download-column'>
      {hasLinear && (
        <OverlayTrigger placement={Placement.TOP} overlay={<Tooltip>linear</Tooltip>}>
          <IconButton
            data-testid={`download-linear-button-${row.cid}`}
            size={Size.LG}
            loading={generatingCSV?.downloadingLinear}
            onClick={() => onDownloadLinear(false)}
          >
            <RiTvLine />
          </IconButton>
        </OverlayTrigger>
      )}
      {hasLinear && enableIconLinearBillingCompanies.includes(row.cid) && (
        <OverlayTrigger placement={Placement.TOP} overlay={<Tooltip>ICON linear networks</Tooltip>}>
          <Button
            data-testid={`download-icon-linear-button-${row.cid}`}
            size={Size.LG}
            loading={generatingCSV?.downloadingIconLinear}
            onClick={() => onDownloadLinear(true)}
          >
            ICON
          </Button>
        </OverlayTrigger>
      )}
      {DOWNLOADABLE_MEDIA_TYPES.filter(mediaType => row.MediaTypes.includes(mediaType)).map(
        mediaType => (
          <OverlayTrigger
            key={mediaType}
            placement={Placement.TOP}
            overlay={<Tooltip>{mediaType}</Tooltip>}
          >
            <IconButton
              data-testid={`download-${mediaType}-button-${row.cid}`}
              size={Size.LG}
              loading={generatingCSV?.downloadingStreaming}
              onClick={() => onDownloadMediaType(mediaType)}
            >
              {(mediaType === MediaType.STREAMING && <FiCast />) ||
                (mediaType === MediaType.AUDIO && <CgHeadset />) ||
                (mediaType === MediaType.DISPLAY && <MdOutlineAdsClick />)}
            </IconButton>
          </OverlayTrigger>
        )
      )}
    </div>
  );
};

interface ComparisonModalProps {
  comparison: DeliveryComparisonRow[];
  onHide: () => void;
}

const ComparisonModal = ({ comparison, onHide }: ComparisonModalProps): ReactNode => (
  <Modal data-testid='comparison-modal' show={comparison.length > 0} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Delivery Comparison</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className='invoice-modal'>
        {comparison.map(
          ({ network, deliveryCost, deliveryCount, invoiceCost, invoiceCount, imps, matched }) => (
            <div className='network-breakdown' key={network}>
              <div className='network-name'>{network}</div>
              <div className='detail'>
                <div className='invoice-modal-label'>Invoice:</div>
                <div className='cost'>{toCost(invoiceCost)}</div>
                <div className='count'>({invoiceCount.toLocaleString()} impressions)</div>
              </div>
              {matched ? (
                <>
                  <div className='detail'>
                    <div className='invoice-modal-label'>Daily Delivery:</div>
                    <div className='cost'>{toCost(deliveryCost)}</div>
                    <div className='count'>({deliveryCount.toLocaleString()} impressions)</div>
                  </div>
                  <div className='detail'>
                    <div className='invoice-modal-label'>Δ:</div>
                    <div className='cost'>{toCost(invoiceCost - deliveryCost)}</div>
                    <div className='count'>
                      ({(invoiceCount - deliveryCount).toLocaleString()} impressions)
                    </div>
                  </div>
                  <div className='imps-header'>Daily discrepancies:</div>
                  {imps.length ? (
                    <Table bordered striped hover size='sm'>
                      <thead>
                        <tr>
                          {imps[0].property && <th>Property</th>}
                          <th>Platform</th>
                          <th>Date</th>
                          <th>Invoice</th>
                          <th>Delivery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {imps.map(
                          ({
                            invoiceCount: rowInvoiceCount,
                            deliveryCount: rowDeliveryCount,
                            notes,
                            platform,
                            date,
                            property
                          }) => {
                            const key = `${network}${platform}${property || 'na'}${date}`;
                            const dataRow = (
                              <tr key={key}>
                                {property && <td>{property}</td>}
                                <td>{platform}</td>
                                <td>{date}</td>
                                <td>
                                  {rowInvoiceCount ? rowInvoiceCount.toLocaleString() : 'no match'}
                                </td>
                                <td>
                                  {rowDeliveryCount
                                    ? rowDeliveryCount.toLocaleString()
                                    : 'no match'}
                                </td>
                              </tr>
                            );

                            if (!notes) return dataRow;

                            return (
                              <OverlayTrigger
                                key={key}
                                placement={Placement.TOP}
                                overlay={
                                  <Popover id={key}>
                                    <Popover.Header>Notes</Popover.Header>
                                    <Popover.Body>{notes}</Popover.Body>
                                  </Popover>
                                }
                              >
                                {dataRow}
                              </OverlayTrigger>
                            );
                          }
                        )}
                      </tbody>
                    </Table>
                  ) : (
                    'No daily impression discrepancies'
                  )}
                </>
              ) : (
                <div className='detail'>
                  <div className='invoice-modal-label'>Daily Delivery:</div>
                  <div className='cost'>none</div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </Modal.Body>
  </Modal>
);

export const Invoicing = () => {
  const setError = useSetError();
  const [invoicingDates, setInvoicingDates] = useState<InvoicingDateRange>(defaultInvoicingDates);
  const {
    data: companyList,
    isLoading: isCompanyListLoading,
    error: companyListError
  } = useInvoicingCompanies(invoicingDates);
  const [checkerMap, setCheckerMap] = useState<CheckerMap>({});
  const [loadingModalCids, setLoadingModalCids] = useState<Record<string, boolean>>({});
  const [modalComparison, setModalComparison] = useState<DeliveryComparisonRow[]>([]);
  const [generatingCSV, setGeneratingCSV] = useState<GeneratingCSVMap>({});
  const [fetchingAll, setFetchingAll] = useState(false);
  const [fetchingSummary, setFetchingSummary] = useState(false);
  const [showManualExportModal, setShowManualExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportSuccessModal, setShowExportSuccessModal] = useState(false);
  const [companySpendTotals, setCompanySpendTotals] = useState<Record<string, number>>();
  const [enableIconLinearBillingCompanies, setEnableIconLinearBillingCompanies] = useState<
    string[]
  >([]);
  const [speed, setSpeed] = useState('3');
  const [showAssetTraffickingModal, setShowAssetTraffickingModal] = useState(false);

  // Runs once on mount. The equivalent bpm-frontend effect listed its own state
  // (enableICONLinearBillingCompanies) as a dependency, refetching forever.
  useEffect(() => {
    (async () => {
      const featureExperimentResponse = await UsersLambdaFetch('/getFeatureExperiments');
      const featureExperimentData =
        await awaitJSON<GetFeatureExperimentsResponse>(featureExperimentResponse);
      setEnableIconLinearBillingCompanies(
        featureExperimentData.company_experiments
          .filter(r => r.experiment_flag === 'enableICONLinearBilling')
          .map(r => r.company)
      );
    })();
  }, []);

  useEffect(() => {
    if (companyListError) {
      setError({ message: companyListError.message, reportError: companyListError });
    }
  }, [companyListError, setError]);

  const renderTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={isCompanyListLoading}
        error={companyListError}
        rowHeight={75}
        headerHeight={50}
      />
    ),
    [isCompanyListLoading, companyListError]
  );

  const fetchCheckerResults = useCallback(
    async (cid: string, start: string, end: string, mediaTypes: MediaTypeType[]) => {
      try {
        const streamingDeliveryMap: Record<string, StreamingDeliveryNetwork[]> = {};
        let invoicingInfo: InvoicingInfo | undefined;

        const promises: Promise<void>[] = [
          ...mediaTypes
            .filter(type => DOWNLOADABLE_MEDIA_TYPES.includes(type))
            .map(async type => {
              const res = await StreamingDeliveryLambdaFetch('/delivery_v2', {
                params: {
                  dimension: 'network',
                  company: cid,
                  include_vod: false,
                  streaming_type: type,
                  start,
                  end
                }
              });
              streamingDeliveryMap[type] = await awaitJSON(res);
            }),
          (async () => {
            const res = await AdminLambdaFetch('/invoicing', {
              params: { start, end, company: cid }
            });
            invoicingInfo = await awaitJSON(res);
          })()
        ];

        if (!companySpendTotals) {
          promises.push(
            (async () => {
              const res = await MiscLambdaFetch('/getESpend', { params: { start, end } });
              const spendInfo = await awaitJSON<{ company: string; sum: string }[]>(res);
              const map: Record<string, number> = {};
              for (const row of spendInfo) {
                map[row.company] = Math.round(parseFloat(row.sum) || 0);
              }
              setCompanySpendTotals(map);
            })()
          );
        }

        await Promise.all(promises);

        // Preserved from bpm-frontend as-is: delivery totals are keyed by media type
        // ("streaming" | "audio" | "display"), but the display channel is read back below
        // under "olv" — a pre-existing mismatch, so olvDeliv is always 0.
        const deliveryTotals: Record<string, number> = {};
        for (const key of Object.keys(streamingDeliveryMap)) {
          for (const network of streamingDeliveryMap[key]) {
            deliveryTotals[key] = (deliveryTotals[key] || 0) + network.cost;
          }
        }

        if (!invoicingInfo) throw new Error('Failed to fetch invoicing info');
        const { needsWizard, linearSpotDetailCount, invoiceSpend } = invoicingInfo;

        setCheckerMap(current => ({
          ...current,
          [cid]: {
            needsWizard,
            linearSpotDetailCount,
            invoiceSpend: Math.round(invoiceSpend),
            strDeliv: Math.round(deliveryTotals.streaming) || 0,
            audioDeliv: Math.round(deliveryTotals.audio) || 0,
            olvDeliv: Math.round(deliveryTotals.olv) || 0
          }
        }));
      } catch (e) {
        setCheckerMap(current => ({ ...current, [cid]: {} }));
        setError({ message: e.message, reportError: e });
      }
    },
    [setError, companySpendTotals]
  );

  const fetchAll = useCallback(async () => {
    if (!companyList) return;
    setFetchingAll(true);

    for (const company of companyList) {
      setCheckerMap(current => ({ ...current, [company.cid]: { checking: true } }));
    }
    const promises = companyList.map(
      company => () =>
        fetchCheckerResults(
          company.cid,
          invoicingDates.start,
          invoicingDates.end,
          company.MediaTypes
        )
    );
    await staggeredAll(promises, parseInt(speed, 10) || 3);
    setFetchingAll(false);
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAMES.INVOICING, action: 'run_checker_all' });
  }, [fetchCheckerResults, companyList, invoicingDates.start, invoicingDates.end, speed]);

  const fetchSummary = useCallback(async () => {
    setFetchingSummary(true);
    try {
      const res = await MiscLambdaFetch('/invoicing_summary', {
        params: { start: invoicingDates.start, end: invoicingDates.end }
      });
      const data = await awaitJSON<InvoicingSummaryCompany[]>(res);
      const summary = buildInvoicingSummaryRows(data);

      download(
        Papa.unparse(summary),
        `Invoicing_Summary_${invoicingDates.start}_${invoicingDates.end}.csv`,
        'text/csv'
      );
      Mixpanel.track(MxE.ActionTriggered, {
        page: PAGE_NAMES.INVOICING,
        action: 'download_summary'
      });
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setFetchingSummary(false);
    }
  }, [invoicingDates.start, invoicingDates.end, setError]);

  const startBillingAutomationExport = useCallback(async () => {
    setIsExporting(true);
    try {
      await DagLambdaFetch('/start_single_job', {
        method: 'POST',
        body: JSON.stringify({
          dag: 'billing-automation-export',
          task: 'BILLING-AUTOMATION-EXPORT',
          task_name: 'BILLING-AUTOMATION-EXPORT',
          override: true,
          extra_base64: { manual: true }
        })
      });
      setShowExportSuccessModal(true);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setIsExporting(false);
    }
  }, [setError]);

  const generateLinearReport = useCallback(
    async ({ company, commission, isIconNetworks }: GenerateLinearReportInput) => {
      const key = isIconNetworks ? 'downloadingIconLinear' : 'downloadingLinear';
      setGeneratingCSV({ [company]: { [key]: true } });

      try {
        // End date is exclusive
        const outerEnd = format(addDays(parseISO(invoicingDates.end), 1), 'yyyy-MM-dd');

        const response = await LinearLambdaFetch('/network_summary', {
          params: {
            company,
            start: invoicingDates.start,
            end: outerEnd,
            calendar: 'true',
            commission,
            isICON: isIconNetworks
          }
        });
        const networkSummary = await awaitJSON<{ csv: string }>(response);
        download(
          networkSummary.csv,
          `${company}-linear-${invoicingDates.start}_${invoicingDates.end}.csv`,
          'text/csv'
        );
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setGeneratingCSV({});
      }
    },
    [invoicingDates.start, invoicingDates.end, setError]
  );

  const generateMediaTypeReport = useCallback(
    async (cid: string, commission: number | undefined, mediaType: MediaTypeType) => {
      setGeneratingCSV({ [cid]: { downloadingStreaming: true } });
      try {
        const lambdaResult = await fetchCometSelfInvoke<
          {
            company: string;
            start: string;
            end: string;
            commission: number | undefined;
            platform: MediaTypeType;
          },
          { filename: string }
        >(AdminLambdaFetch, '/invoicing/generate', {
          company: cid,
          start: invoicingDates.start,
          end: invoicingDates.end,
          commission,
          platform: mediaType
        });
        await pollS3({
          bucket: 'bpm-invoicing',
          mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          filename: `${lambdaResult.filename}.xlsx`
        });
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setGeneratingCSV({});
      }
    },
    [invoicingDates.start, invoicingDates.end, setError]
  );

  const showComparison = useCallback(
    async (cid: string) => {
      setLoadingModalCids(current => ({ ...current, [cid]: true }));
      try {
        const res = await AdminLambdaFetch('/invoicing/check_delivery', {
          params: { start: invoicingDates.start, end: invoicingDates.end, company: cid }
        });
        const compareData = await awaitJSON<DeliveryComparisonRow[]>(res);
        setModalComparison(compareData);
        Mixpanel.track(MxE.ModalOpened, {
          page: PAGE_NAMES.INVOICING,
          modal: 'delivery_comparison'
        });
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setLoadingModalCids(current => ({ ...current, [cid]: false }));
      }
    },
    [invoicingDates.start, invoicingDates.end, setError]
  );

  const headers: TypedHeader<InvoicingCompany>[] = [
    {
      label: 'Company',
      name: 'name',
      flex: 1,
      minFlexWidth: 260,
      renderer: row => <CompanyCell cid={row.cid} name={row.name} />
    },
    {
      label: 'Checker Results',
      name: 'checker_results',
      nonInteractive: true,
      width: 480,
      renderer: row => (
        <CheckerResultsCell
          row={row}
          checkerInfo={checkerMap[row.cid]}
          companySpendTotal={companySpendTotals?.[row.cid] ?? 0}
          loading={Boolean(loadingModalCids[row.cid])}
          onRecheck={() => {
            setCheckerMap(current => ({ ...current, [row.cid]: { checking: true } }));
            fetchCheckerResults(row.cid, invoicingDates.start, invoicingDates.end, row.MediaTypes);
            Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAMES.INVOICING, action: 'recheck' });
          }}
          onShowComparison={() => showComparison(row.cid)}
        />
      )
    },
    {
      label: 'Download',
      name: 'MediaTypes',
      width: 200,
      nonInteractive: true,
      renderer: row => (
        <DownloadColumnCell
          row={row}
          enableIconLinearBillingCompanies={enableIconLinearBillingCompanies}
          generatingCSV={generatingCSV[row.cid]}
          onDownloadLinear={isIconNetworks =>
            generateLinearReport({ company: row.cid, commission: row.commission, isIconNetworks })
          }
          onDownloadMediaType={mediaType =>
            generateMediaTypeReport(row.cid, row.commission, mediaType)
          }
        />
      )
    }
  ];

  return (
    <Page
      title={PAGE_NAMES.INVOICING}
      pageType={PAGE_NAMES.INVOICING}
      isLoading={isCompanyListLoading}
      actions={
        <div className='invoicing-page-actions'>
          <Button
            onClick={() => setShowManualExportModal(true)}
            disabled={showManualExportModal || isExporting}
          >
            Trigger Billing Automation
          </Button>
          <Button onClick={() => setShowAssetTraffickingModal(true)}>
            Upload Asset Trafficking Fees
          </Button>
          <Button onClick={fetchAll} disabled={fetchingAll}>
            Fetch All
          </Button>
          <OverlayTrigger
            placement={Placement.BOTTOM_END}
            overlay={
              <Tooltip>
                Increase to fetch all faster. Decrease if it makes your computer sad.
              </Tooltip>
            }
          >
            <Form.Control
              className='speed'
              placeholder='speed'
              type='number'
              value={speed}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSpeed(e.currentTarget.value)}
            />
          </OverlayTrigger>
          <Button onClick={fetchSummary} disabled={fetchingSummary}>
            Summary
          </Button>
          <DatePicker range={invoicingDates} onChange={setInvoicingDates} />
        </div>
      }
    >
      <>
        <TablePage tableClassName='invoicing-table'>
          <BPMTable
            data={companyList ?? []}
            headerHeight={50}
            rowHeight={75}
            headers={headers}
            noRowsRenderer={renderTableLoading}
          />
          <ComparisonModal comparison={modalComparison} onHide={() => setModalComparison([])} />
        </TablePage>
        {showManualExportModal && (
          <AlertDialog
            show={showManualExportModal}
            onHide={() => {
              setShowManualExportModal(false);
            }}
            intent={Intent.WARNING}
            icon={<MdSend size={40} />}
            title='Trigger Manual Export?'
            body='Are you sure you want to trigger billing automation exports across clients?'
            primaryAction={{ onClick: startBillingAutomationExport, label: 'Trigger' }}
            secondaryAction={{ onClick: () => {} }}
          />
        )}
        {showExportSuccessModal && (
          <AlertDialog
            show={showExportSuccessModal}
            onHide={() => setShowExportSuccessModal(false)}
            intent={Intent.SUCCESS}
            icon={<MdCheck size={40} />}
            title='Export Triggered'
            body='The billing automation export job has been triggered.'
          />
        )}
        {showAssetTraffickingModal && (
          <AssetTraffickingUploadModal onHide={() => setShowAssetTraffickingModal(false)} />
        )}
      </>
    </Page>
  );
};
